package com.evoapps.evoshift.ui.finance

import com.evoapps.evoshift.ui.util.dayInSentence
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.TrendingUp
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedCard
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.LocalContentColor
import com.evoapps.evoshift.ui.LightGold
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.size
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.domain.forecast.DayForecast
import com.evoapps.evoshift.domain.forecast.ForecastBasisKind
import com.evoapps.evoshift.domain.forecast.HeadcountSuggestion
import com.evoapps.evoshift.domain.forecast.SalesPerHourSource
import com.evoapps.evoshift.domain.usecase.finance.DaySuggestion
import com.evoapps.evoshift.ui.util.formatHintHours
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.forecast_banner
import evoshift.composeapp.generated.resources.forecast_basis_holiday
import evoshift.composeapp.generated.resources.forecast_basis_trading_sunday
import evoshift.composeapp.generated.resources.forecast_basis_weekday
import evoshift.composeapp.generated.resources.forecast_headcount
import evoshift.composeapp.generated.resources.forecast_headcount_slots
import evoshift.composeapp.generated.resources.forecast_rate_goal
import evoshift.composeapp.generated.resources.forecast_rate_history
import evoshift.composeapp.generated.resources.forecast_suggested
import evoshift.composeapp.generated.resources.forecast_trend
import evoshift.composeapp.generated.resources.forecast_trend_none
import evoshift.composeapp.generated.resources.forecast_use
import evoshift.composeapp.generated.resources.forecast_use_all
import org.jetbrains.compose.resources.stringResource

/*
 * SH-120: the sales forecast's words. The domain gives the facts (DayForecast, HeadcountSuggestion);
 * here they become "Average takings of the last 6 days like this one (Friday): 5 000 zł. Trend of
 * the last 4 weeks: +4%." and "About 20 h of work at 250 zł per labour hour (your last 8 weeks).
 * People: Morning 2 · Afternoon 2."
 */

/** "+4%", "−3%". */
fun formatSignedPercent(percent: Int): String =
    (if (percent > 0) "+" else if (percent < 0) "−" else "") + formatPercent(kotlin.math.abs(percent).toDouble())

/** Where the suggested amount comes from, one sentence per fact. */
@Composable
fun forecastBasisLines(forecast: DayForecast, currency: String): List<String> = buildList {
    val basis = forecast.basis
    val average = formatMoney(basis.average, currency)
    add(
        when (basis.kind) {
            ForecastBasisKind.WEEKDAY -> stringResource(Res.string.forecast_basis_weekday, basis.sameDays, dayInSentence(basis.dayOfWeek), average)
            ForecastBasisKind.TRADING_SUNDAY -> stringResource(Res.string.forecast_basis_trading_sunday, basis.sameDays, average)
            ForecastBasisKind.HOLIDAY -> stringResource(Res.string.forecast_basis_weekday, basis.sameDays, dayInSentence(basis.dayOfWeek), average)
        }
    )
    if (basis.kind == ForecastBasisKind.HOLIDAY) {
        add(stringResource(Res.string.forecast_basis_holiday, basis.pastHolidays, formatSignedPercent(basis.holidayPercent ?: 0)))
    }
    add(
        if (basis.trendPercent == 0) stringResource(Res.string.forecast_trend_none)
        else stringResource(Res.string.forecast_trend, formatSignedPercent(basis.trendPercent))
    )
}

/** The hours and people the suggested sales carry. */
@Composable
fun headcountLines(headcount: HeadcountSuggestion, currency: String): List<String> = buildList {
    add(
        stringResource(
            Res.string.forecast_headcount,
            formatHintHours(headcount.labourHours),
            formatMoney(headcount.salesPerHour, currency),
            stringResource(if (headcount.source == SalesPerHourSource.GOAL) Res.string.forecast_rate_goal else Res.string.forecast_rate_history)
        )
    )
    if (headcount.slots.isNotEmpty()) {
        add(stringResource(Res.string.forecast_headcount_slots, headcount.slots.joinToString(" · ") { "${it.label} ${it.people}" }))
    }
}

/**
 * The day's suggestion in the target dialog: the amount, why, the labour it carries, and "Use"
 * that puts it in the field (still editable before saving).
 */
@Composable
fun ForecastSuggestion(
    suggestion: DaySuggestion,
    currency: String,
    onUse: () -> Unit,
    // The dialog's navy body gives white text (EvoDialog): not onSurfaceVariant, dark navy there.
    color: Color = LocalContentColor.current.copy(alpha = 0.85f)
) {
    Column(Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(2.dp)) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Text(
                stringResource(Res.string.forecast_suggested, formatMoney(suggestion.amount, currency)),
                style = MaterialTheme.typography.bodyMedium,
                fontWeight = FontWeight.SemiBold,
                color = color,
                modifier = Modifier.weight(1f)
            )
            TextButton(onClick = onUse, colors = ButtonDefaults.textButtonColors(contentColor = LightGold)) { Text(stringResource(Res.string.forecast_use)) }
        }
        (forecastBasisLines(suggestion.forecast, currency) + suggestion.headcount?.let { headcountLines(it, currency) }.orEmpty()).forEach {
            Text(it, style = MaterialTheme.typography.bodySmall, color = color)
        }
    }
}

/** The month's offer: how many days have a suggested target, and "Use suggested targets". */
@Composable
fun ForecastBanner(days: Int, onUseAll: () -> Unit, modifier: Modifier = Modifier) {
    OutlinedCard(modifier.fillMaxWidth()) {
        Row(Modifier.padding(start = 12.dp, end = 4.dp, top = 4.dp, bottom = 4.dp), verticalAlignment = Alignment.CenterVertically) {
            Icon(Icons.AutoMirrored.Filled.TrendingUp, contentDescription = null, modifier = Modifier.size(18.dp), tint = MaterialTheme.colorScheme.primary)
            Text(
                stringResource(Res.string.forecast_banner, days),
                style = MaterialTheme.typography.bodySmall,
                modifier = Modifier.weight(1f).padding(horizontal = 8.dp)
            )
            TextButton(onClick = onUseAll) { Text(stringResource(Res.string.forecast_use_all)) }
        }
    }
}
